import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  verifyEmail: async (email, code) => {
    const response = await api.post('/auth/verify-email', null, {
      params: { email, code }
    });
    return response.data;
  },
  resendVerificationCode: async (email) => {
    const response = await api.post('/auth/resend-verification-code', null, {
      params: { email }
    });
    return response.data;
  },
  sendLoginCode: async (email) => {
    const response = await api.post('/auth/send-login-code', { email });
    return response.data;
  },
  loginWithCode: async (email, code) => {
    const response = await api.post('/auth/login-with-code', { email, code });
    return response.data;
  },
  getOfferStatus: async () => {
    const response = await api.get('/auth/promo-status');
    return response.data;
  },
};

// Profile API
export const profileAPI = {
  getProfiles: async (filters = {}) => {
    const response = await api.get('/profiles', { params: filters });
    return response.data;
  },
  getProfile: async (profileId) => {
    const response = await api.get(`/profiles/${profileId}`);
    return response.data;
  },
  updateProfile: async (profileId, data) => {
    const response = await api.put(`/profiles/${profileId}`, data);
    return response.data;
  },
  getShortlist: async () => {
    const response = await api.get('/user/shortlist');
    return response.data;
  },
  addToShortlist: async (profileId) => {
    const response = await api.post(`/profiles/${profileId}/shortlist`);
    return response.data;
  },
  removeFromShortlist: async (profileId) => {
    const response = await api.delete(`/profiles/${profileId}/shortlist`);
    return response.data;
  },
  sendInterest: async (profileId) => {
    const response = await api.post(`/interests/send?receiver_id=${profileId}`);
    return response.data;
  },
};

// User API
export const userAPI = {
  getCurrentUser: async () => {
    const response = await api.get('/user/me');
    return response.data;
  },
  updateProfile: async (profileData) => {
    const response = await api.put('/user/profile', profileData);
    return response.data;
  },
  getMatches: async () => {
    const response = await api.get('/user/matches');
    return response.data;
  },
  getShortlist: async () => {
    const response = await api.get('/user/shortlist');
    return response.data;
  },
  getInterests: async () => {
    const response = await api.get('/user/interests');
    return response.data;
  },
  getMessages: async () => {
    const response = await api.get('/user/messages');
    return response.data;
  },
  sendMessage: async (messageData) => {
    const response = await api.post('/user/messages', messageData);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/user/stats');
    return response.data;
  },
};

// Premium API
export const premiumAPI = {
  getPlans: async () => {
    const response = await api.get('/premium/plans');
    return response.data;
  },
  subscribe: async (planId) => {
    const response = await api.post(`/premium/subscribe/${planId}`);
    return response.data;
  },
  getStatus: async () => {
    const response = await api.get('/premium/status');
    return response.data;
  },
  trackContactView: async () => {
    const response = await api.post('/premium/track-contact-view');
    return response.data;
  },
  cancelSubscription: async () => {
    const response = await api.post('/premium/cancel-subscription');
    return response.data;
  }
};

// Interest API
export const interestAPI = {
  getReceived: async () => {
    const response = await api.get('/interests/received');
    return response.data;
  },
  getSent: async () => {
    const response = await api.get('/interests/sent');
    return response.data;
  },
  respond: async (interestId, status) => {
    // Backend uses /accept or /decline endpoints
    const endpoint = status === 'accepted' ? 'accept' : 'decline';
    const response = await api.put(`/interests/${interestId}/${endpoint}`);
    return response.data;
  },
  getStats: async () => {
    // Get counts from received endpoint
    const response = await api.get('/interests/count/received');
    return response.data;
  }
};

// Messages API
export const messagesAPI = {
  getConversations: async () => {
    const response = await api.get('/messages/conversations');
    return response.data;
  },
  getMessages: async (otherUserId) => {
    const response = await api.get(`/messages/conversation/${otherUserId}`);
    return response.data;
  },
  sendMessage: async (receiverId, content) => {
    const response = await api.post('/messages/send', { 
      receiverId: receiverId,
      content: content
    });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/messages/unread-count');
    return response.data;
  }
};

export default api;